import React, {useMemo} from 'react';
import {DashboardLayout, PrimaryButton} from '../../../components';
import {
  biodataIc,
  failed_t,
  ipDeviceCheckIc,
} from '../../../assets/images/images';
import {useParams, useLocation, useNavigate} from 'react-router-dom';
import {Constants} from '../../../helpers';
import ReusableLink from '../../Verifications/ReusableLink';
import {getStatusTextAndStyles} from '../../../helpers/getStatusTextAndStyles';
import ActivityLog from '../../../components/ActivityLog';
import {useQuery} from 'react-query';
import {fetchAuthenticationDetails} from '../../../requests/queries/easyAuth';
import moment from 'moment';
import Breadcrumbs from 'components/BreadCrumbs';

function Layout({children}) {
  const {slug} = useParams();
  const {state} = useLocation();
  const isRegistration = state?.isRegistration || false;

  const navigate = useNavigate();

  const {data: authData, isLoading} = useQuery(
    ['auth-summary', slug],
    () => fetchAuthenticationDetails({authenticationId: slug, type: 'summary'}),
    {
      retry: 1,
    },
  );

  const session_summary = useMemo(
    () => !isLoading && authData,
    [authData, isLoading],
  );

  const VERIFICATION = session_summary?.data?.Verification;
  const AUTH_USER = session_summary?.data?.auth;
  const vStatus = VERIFICATION?.status?.toLowerCase();

  const LINKS = [
    {
      path: Constants.PAGES_URL.AUTHENTICATIONS_BIO_DATA,
      src: biodataIc,
      text: 'Bio data',
    },
    {
      path: Constants.PAGES_URL.AUTHENTICATIONS_IP_DEVICE_CHECK,
      src: ipDeviceCheckIc,
      text: 'IP/Device Check',
    },
    // {
    //   path: Constants.PAGES_URL.AUTHENTICATIONS_BEHAVIORAL_SCORE,
    //   src: behaviorIc,
    //   text: 'Behavioral Score',
    // },
  ];

  return (
    <DashboardLayout
      bg="bg-white xl:bg-white80"
      topPadding="pt-0"
      xlLeftMargin="xl:ml-12"
      fullScreen
      overFlow=""
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">
          {!isRegistration ? 'Authentications' : 'Customers'}
        </p>
      }
    >
      <div className="flex items-center min-h-[52px] px-8 mx-[-32px] py-2 flex-wrap gap-3 justify-between border border-transparent border-b-[#E4E4E6]">
        <Breadcrumbs
          mainText={!isRegistration ? 'Authentication' : 'Customer'}
          mainTextPath={
            !isRegistration
              ? Constants.PAGES_URL.AUTHENTICATIONS
              : Constants.PAGES_URL.AUTHENTICATION_CUSTOMERS
          }
          items={[
            {
              label: !isRegistration ? 'Details' : 'Registration',
            },
          ]}
        />
      </div>
      <div className="flex-wrap items-start min-h-full xl:flex xl:flex-nowrap basis-full">
        <div className="basis-[17.14%] flex flex-col gap-6 pt-16 sm:pt-6 xl:pt-[35px] xl:sticky xl:top-[35px]">
          <h2 className="hidden text-base font-medium sm:block text-grey sm:text-xl whitespace-nowrap">
            {!isRegistration ? 'Authentication' : 'Registration'} Details
          </h2>
          <div className="flex items-center justify-between sm:hidden">
            <h2 className="text-base font-medium sm:hidden text-grey sm:text-xl whitespace-nowrap">
              {!isRegistration ? 'Authentication' : 'Registration'}
            </h2>
          </div>
          <div className="border-b border-grey60 xl:border-none">
            <div className="flex gap-2 xl:p-2 xl:flex-col">
              {LINKS.map(({path, src, text}) => (
                <ReusableLink
                  key={path}
                  path={path.replace(':slug', slug)}
                  src={src}
                  text={text}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="xl:ml-[37px] xl:basis-[45.67%] mt-6 xl:mt-0 px-4 grow xl:grow-0 xl:p-8 pt-4 xl:pt-[36px] bg-white80 xl:bg-white">
          {children}
        </div>

        <div className="xl:basis-[36.04%] xl:px-6 py-10 xl:sticky xl:top-[35px] bg-white xl:bg-white80">
          <div className="flex justify-between items-center">
            <div
              className={`flex uppercase pt-[5px] pb-[3px] px-2 text-xs  font-medium ${
                getStatusTextAndStyles(vStatus).styles
              } rounded-full w-fit items-start gap-[2px]`}
            >
              {getStatusTextAndStyles(vStatus).text}
            </div>

            {isRegistration && (
              <PrimaryButton
                buttonText="Authentication Analysis"
                yPadding="py-2"
                onClick={() =>
                  navigate(
                    Constants.PAGES_URL.AUTHENTICATION_ANALYTICS.replace(
                      ':slug',
                      VERIFICATION?.reference_id,
                    ),
                  )
                }
              />
            )}
          </div>
          <div className="grid lg:grid-cols-2 grid-cols-1 gap-2">
            <div className="flex flex-col gap-1 p-4 mt-4 text-xs uppercase rounded bg-white80 xl:bg-white text-body">
              <span className="font-medium">
                {isRegistration ? 'User Id' : 'Auth ID'}
              </span>
              <span>
                {isRegistration
                  ? VERIFICATION?.customer_id || 'N/A'
                  : slug || 'N/A'}
              </span>
            </div>
            <div className="flex flex-col gap-1 p-4 mt-4 text-xs uppercase rounded bg-white80 xl:bg-white text-body">
              <span className="font-medium">Session ID</span>
              <span>{VERIFICATION?.session_id || '-'}</span>
            </div>
          </div>
          <div className="w-full p-4 mt-4 rounded bg-white80 xl:bg-white">
            <p className="mt-2 text-xs font-medium uppercase text-grey">
              Total time spent
            </p>
            <p className="mt-2 text-xs text-body">
              The amount of time taken to process authentication details
            </p>
            <p className="mt-2 text-xl font-medium text-grey">
              {VERIFICATION?.completion_time
                ?.replace('m', ':')
                .replace('s', '') || '00:00'}
            </p>
          </div>
          <div className="p-4 bg-white items-stretch  mt-4 w-full">
            <p className="mt-2 text-xs font-medium uppercase text-grey">
              date/time created
            </p>
            <div className="mt-2 flex rounded w-full">
              <p className="font-medium text-grey">
                {VERIFICATION && VERIFICATION.date_created
                  ? moment(VERIFICATION.date_created).format('Do MMMM YYYY')
                  : isLoading
                  ? '-'
                  : '-'}
              </p>

              <p className=" font-medium text-grey">
                ,{' '}
                {VERIFICATION && VERIFICATION.date_created
                  ? moment(VERIFICATION.date_created).format('hh:mm A')
                  : isLoading
                  ? '-'
                  : '-'}
              </p>
            </div>
          </div>
          {(vStatus === 'pending' || vStatus === 'failed') &&
            AUTH_USER?.reason && (
              <div className="w-full p-4 rounded bg-white80 xl:bg-white mt-4">
                <div className="flex items-center mb-2">
                  <img src={failed_t} alt="" />
                  <p className="ml-2 text-xs font-medium uppercase text-grey">
                    reason for {vStatus} verification
                  </p>
                </div>
                <p className="mt-2 text-sm text-body">
                  {AUTH_USER?.reason || '-'}
                </p>
              </div>
            )}
          <ActivityLog />
        </div>
      </div>
    </DashboardLayout>
  );
}

export default Layout;
